import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NoServiceAccounts from 'src/assets/no-service-accounts.svg';
import Button from 'src/components/ui/Button/Button';
import EmptyState from 'src/components/ui/EmptyState/EmptyState';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import Table from 'src/components/ui/Table/Table';
import {
  TableHeader,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import TablePagination from 'src/components/ui/Table/TablePagination/TablePagination';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { routes } from 'src/routes';
import {
  getCompanyList,
  setCompanyList,
} from 'src/store/actions/companyActions';
import { ListCompany } from 'src/store/models/Company';
import { getAddress } from 'src/utils/addressUtils';
import { getCompanyName } from 'src/utils/companyUtils';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useQueryParams } from 'src/utils/useQueryParams';

import classes from './Companies.module.scss';

const Companies: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const companyList = useAppSelector((state) => state.company.companyList);
  const loading = useLoadingSelector({
    actionType: getCompanyList.toString(),
  });

  const headers: TableHeader<ListCompany>[] = [
    {
      accessor: 'title',
      label: t('common:company'),
      sortable: true,
      component: (company) => getCompanyName(company),
    },
    {
      accessor: 'personnelCount',
      label: t('common:personnel'),
      sortable: false,
    },
    {
      accessor: 'line1',
      label: t('common:address'),
      component: (company) => getAddress(company),
    },
    {
      accessor: 'eMail',
      label: t('common:email'),
      sortable: true,
    },
    {
      accessor: 'phone',
      label: t('common:phone'),
      sortable: true,
    },
  ];

  const { urlParams } = useQueryParams<TableQueryParameters>(location.search, {
    ...paginationDefaultState,
  });

  useEffect(() => {
    dispatch(setCompanyList({ count: 0, data: [], ctx: null }));
    return () => {
      dispatch(setCompanyList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getCompanyList({ params: urlParams }));
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('service_account:service_companies')}
        endNode={
          <Button
            onClick={() => navigate(routes.NEW_COMPANY)}
            endIcon={<FontAwesomeIcon icon={faPlus} />}
            className={classes.HeaderButton}
          >
            {t('service_account:add_new_company')}
          </Button>
        }
      />
      <Table
        headers={headers}
        data={companyList?.data}
        search
        loading={loading}
        className={classNames(classes.Table, { [classes.Loading]: loading })}
        classes={{ header: classes.TableHeader }}
        onRowClick={(row) =>
          !row?.base ? navigate(`${routes.EDIT_COMPANY}/${row?.id}`) : undefined
        }
        emptyTableComponent={
          <EmptyState
            title={t('service_account:no_companies')}
            subtitle={t('service_account:no_companies_details')}
            icon={NoServiceAccounts}
          />
        }
        searchPlaceholder={t('common:search_field')}
      />
      <TablePagination
        className={classes.Pagination}
        urlParams={urlParams}
        total={companyList?.count}
      />
    </div>
  );
};

export default Companies;
